import React, { useCallback, useEffect, useRef } from "react";
import { PropsOf } from "@emotion/react";

import { useMiniChatPopupManager, useMinimizeChatList } from "@/frameworks/core/hooks";
import { useChatRoomListActions } from "@/frameworks/core/chat";
import { MiniChatPopupComponent } from "../MiniChatPopup";

type MiniChatPopupProps = Omit<PropsOf<typeof MiniChatPopupComponent>, "chatLayoutRef">;

export default function withOutsideViewportInChat<T extends MiniChatPopupProps>(
	WrappedComponent: React.ComponentType<T & { chatLayoutRef: React.RefObject<HTMLDivElement> }>
) {
	return function WithOutsideViewportInChatComponent(props: T) {
		const viewportCheckRef = useRef<ReturnType<typeof setTimeout> | null>(null);
		const chatLayoutRef = useRef<HTMLDivElement>(null);

		const { miniChatPopupActions } = useMiniChatPopupManager();
		const { minimizeChatListActions } = useMinimizeChatList();
		const { getDMRoom } = useChatRoomListActions();

		// 뷰포트 체크 함수
		const checkViewport = useCallback(() => {
			if (!chatLayoutRef.current) return;

			const rect = chatLayoutRef.current.getBoundingClientRect();
			const isVisible =
				rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;

			// 뷰포트에서 벗어났을 때만 숨김 처리
			if (!isVisible) {
				if (!miniChatPopupActions.get({ ...props })) return;
				miniChatPopupActions.hide(props.id);

				if (["Space", "DM"].includes(props.type)) {
					switch (props.type) {
						case "Space":
							{
								minimizeChatListActions.append({
									id: props.id,
									spaceID: props.spaceID ?? "",
									spaceName: props.spaceName ?? "",
									type: "Space",
									size: props?.size
								});
							}
							break;
						case "DM": {
							const dmRoom = getDMRoom(props.id);
							if (!dmRoom) return;
							const channelUserList = dmRoom.users.map((user) => ({
								name: user.name,
								src: user.profileImagePath,
								userID: user.id
							}));
							minimizeChatListActions.append({
								type: "DM",
								users: channelUserList,
								id: props.id,
								size: props?.size
							});
							break;
						}
						default:
							break;
					}
				}
			}
		}, [props, miniChatPopupActions, minimizeChatListActions]);

		// 스크롤 및 리사이즈 이벤트 리스너
		useEffect(() => {
			const handleScroll = () => {
				if (viewportCheckRef.current) {
					clearTimeout(viewportCheckRef.current);
				}
				viewportCheckRef.current = setTimeout(checkViewport, 100);
			};

			const handleResize = () => {
				if (viewportCheckRef.current) {
					clearTimeout(viewportCheckRef.current);
				}
				viewportCheckRef.current = setTimeout(checkViewport, 100);
			};

			window.addEventListener("scroll", handleScroll, { passive: true });
			window.addEventListener("resize", handleResize, { passive: true });

			// 초기 체크
			checkViewport();

			return () => {
				window.removeEventListener("scroll", handleScroll);
				window.removeEventListener("resize", handleResize);
				if (viewportCheckRef.current) {
					clearTimeout(viewportCheckRef.current);
				}
			};
		}, [checkViewport]);

		return <WrappedComponent {...props} chatLayoutRef={chatLayoutRef} />;
	};
}
